import { useState, useRef, useEffect } from 'react';
import { Search, MapPin, X, Loader2, Sparkles } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from '@/hooks/use-toast';
import { log } from '@/utils/logger';

interface EnhancedSearchBarProps {
  onSearch: (query: string, coordinates?: [number, number]) => Promise<void>;
  placeholder?: string;
  value?: string;
  onChange?: (value: string) => void;
  onFocus?: () => void;
  onBlur?: () => void;
  className?: string;
  /** Slimmer desktop version that fills its container */
  compact?: boolean;
}

// Mobile-first responsive design with proper touch targets
export function EnhancedSearchBar({ 
  onSearch, 
  placeholder = "Search destinations, cities, or experiences...",
  value = '',
  onChange,
  onFocus,
  onBlur,
  className,
  compact = false
}: EnhancedSearchBarProps) {
  const logger = log.component('EnhancedSearchBar');
  
  const [searchValue, setSearchValue] = useState(value);
  const [isSearching, setIsSearching] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  
  const inputRef = useRef<HTMLInputElement>(null);

  // Detect mobile viewport
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Sync external value changes
  useEffect(() => {
    if (value !== searchValue) {
      setSearchValue(value);
    }
  }, [value]);

  const handleSearch = async (searchQuery?: string) => {
    const query = searchQuery || searchValue.trim();
    
    if (!query) {
      toast({
        title: "Search Error",
        description: "Please enter a location to search",
        variant: "destructive",
      });
      return;
    }

    setIsSearching(true);
    
    try {
      await onSearch(query);
      
      // On mobile, blur to hide keyboard after search
      if (isMobile && inputRef.current) {
        inputRef.current.blur();
      }

    } catch (error) {
      logger.error('Search execution failed', error);
      toast({
        title: "Search Error",
        description: "Error searching location. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsSearching(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSearch();
    }
    
    // Handle escape key to blur on mobile
    if (e.key === 'Escape' && isMobile && inputRef.current) {
      inputRef.current.blur();
    }
  };

  const handleQueryChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setSearchValue(newValue);
    
    // Notify parent of immediate change for UI feedback
    if (onChange) {
      onChange(newValue);
    }
  };

  const handleClear = () => {
    setSearchValue('');
    if (onChange) {
      onChange('');
    }
    if (inputRef.current) {
      inputRef.current.focus();
    }
  };

  const handleFocus = () => {
    setIsFocused(true);
    onFocus?.();
  };

  const handleBlur = () => {
    setIsFocused(false);
    onBlur?.();
  };

  return (
    <motion.div 
      className={cn(
        // Mobile-first responsive container
        "relative w-full mx-auto",
        "max-w-xs sm:max-w-lg md:max-w-2xl lg:max-w-4xl", // Progressive width scaling
        "flex items-center bg-white/98 backdrop-blur-xl",
        // Mobile-friendly border radius - less rounded on mobile
        "rounded-2xl sm:rounded-3xl",
        "shadow-lg sm:shadow-2xl hover:shadow-xl sm:hover:shadow-3xl",
        "transition-all duration-300 sm:duration-500",
        "border border-white/20 overflow-hidden",
        // Ensure proper height consistency and padding
        "min-h-[48px] sm:min-h-[64px]",
        "pr-0", // Remove container padding to let button position naturally
        // Mobile-specific focus states
        isFocused && [
          "ring-2 sm:ring-4 ring-emerald-400/40",
          "shadow-emerald-500/20 sm:shadow-emerald-500/25",
          !compact && (isMobile ? "scale-[1.01]" : "scale-[1.02]")
        ],
        className,
        compact && "sm:max-w-none md:max-w-none lg:max-w-none sm:rounded-lg sm:shadow-none sm:hover:shadow-none sm:min-h-[48px]"
      )}
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, ease: "easeOut" }}
    >

      {/* Search Icon Section */}
      <motion.div
        className={cn(
          "absolute left-3 sm:left-6 flex items-center justify-center",
          "w-8 h-8 sm:w-12 sm:h-12", // Container size
          compact && "sm:left-2 sm:w-10 sm:h-10"
        )}
        initial={{ scale: 1 }}
        animate={{ 
          scale: isSearching ? 1.1 : 1,
          rotate: isSearching ? 360 : 0
        }}
        transition={{ 
          duration: isSearching ? 2 : 0.3,
          repeat: isSearching ? Infinity : 0,
          ease: "linear"
        }}
      >
        {isSearching ? (
          <Loader2 className="h-5 w-5 sm:h-6 sm:w-6 text-emerald-600" />
        ) : (
          <div className="relative flex items-center justify-center">
            <MapPin className="h-5 w-5 sm:h-6 sm:w-6 text-emerald-600" />
            {isFocused && !isMobile && (
              <motion.div
                className="absolute -top-1 -right-1"
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ duration: 0.3, delay: 0.1 }}
              >
                <Sparkles className="h-2 w-2 sm:h-3 sm:w-3 text-amber-500" />
              </motion.div>
            )}
          </div>
        )}
      </motion.div>

      {/* Input Field */}
      <input
        ref={inputRef}
        type="text"
        placeholder={isMobile ? "Search destinations..." : placeholder}
        value={searchValue}
        onChange={handleQueryChange}
        onKeyDown={handleKeyDown}
        onFocus={handleFocus}
        onBlur={handleBlur}
        className={cn(
          "flex-1 bg-transparent border-none outline-none",
          // Mobile-optimized padding and font sizes
          "pl-12 sm:pl-20 pr-12 sm:pr-16", // Reset to original padding
          "h-12 sm:h-16", // Consistent with button height
          "text-base sm:text-xl text-gray-800 font-medium",
          "placeholder:text-gray-400 placeholder:text-sm sm:placeholder:text-base",
          "transition-all duration-300",
          // Mobile-specific input improvements
          "touch-manipulation",
          // Prevent zoom on iOS and ensure proper alignment
          "text-[16px] sm:text-xl leading-normal",
          compact && "sm:pl-14 sm:pr-14 sm:h-12 sm:text-base"
        )}
        aria-label={placeholder || "Search locations"}
        // Mobile keyboard optimizations
        autoComplete="off"
        autoCorrect="off"
        autoCapitalize="off"
        spellCheck="false"
        inputMode="search"
      />

      {/* Clear Button */}
      <AnimatePresence>
        {searchValue && (
          <motion.button 
            initial={{ opacity: 0, scale: 0.8, x: 10 }}
            animate={{ opacity: 1, scale: 1, x: 0 }}
            exit={{ opacity: 0, scale: 0.8, x: 10 }}
            transition={{ duration: 0.2 }}
            className={cn(
              "absolute right-12 sm:right-16", // Reset to original position
              compact && "sm:right-32",
              "p-2 hover:bg-gray-100 rounded-full",
              "transition-all duration-200 group",
              "min-h-[44px] min-w-[44px] flex items-center justify-center", // Mobile touch target
              "touch-manipulation"
            )}
            onClick={handleClear}
            type="button"
            aria-label="Clear search"
          >
            <X className="h-4 w-4 sm:h-5 sm:w-5 text-gray-400 group-hover:text-gray-600" />
          </motion.button>
        )}
      </AnimatePresence>
    
      {/* Search Button */}
      <Button
        onClick={() => handleSearch()}
        disabled={isSearching}
        className={cn(
          // Mobile-first button sizing - more prominent than fullscreen version
          "h-12 sm:h-16",
          "w-12 sm:w-auto sm:px-10", // Fixed width on mobile like MobileFullscreenSearch but larger
          "rounded-r-2xl sm:rounded-r-3xl",
          "transition-all duration-300 sm:duration-500",
          "bg-gradient-to-r from-emerald-600 to-emerald-700",
          "hover:from-emerald-700 hover:to-emerald-800",
          "text-white shadow-lg hover:shadow-xl sm:shadow-xl sm:hover:shadow-2xl",
          "focus:ring-2 focus:ring-offset-2 focus:ring-emerald-500",
          "flex items-center justify-center gap-1.5 sm:gap-3 group",
          "text-sm sm:text-lg font-semibold tracking-wide",
          "transform hover:scale-105 active:scale-95",
          "min-h-[48px] min-w-[48px]", // Good touch targets
          "touch-manipulation shrink-0",
          compact && "sm:h-12 sm:px-7 sm:text-base sm:rounded-r-lg sm:shadow-none sm:hover:shadow-none hover:scale-100 active:scale-100",
          isSearching && "opacity-75 cursor-not-allowed"
        )}
      >
        <AnimatePresence mode="wait">
          {isSearching ? (
            <motion.div
              key="loading"
              initial={{ opacity: 0, rotate: -90 }}
              animate={{ opacity: 1, rotate: 0 }}
              exit={{ opacity: 0, rotate: 90 }}
              transition={{ duration: 0.2 }}
              className="flex items-center justify-center gap-1 sm:gap-2"
            >
              <Loader2 className="h-5 w-5 sm:h-5 sm:w-5 animate-spin" />
              <span className="hidden sm:inline">Loading...</span>
            </motion.div>
          ) : (
            <motion.div
              key="search"
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.8 }}
              transition={{ duration: 0.2 }}
              className="flex items-center justify-center gap-1 sm:gap-2"
            >
              <Search className="h-5 w-5 sm:h-5 sm:w-5 group-hover:scale-110 transition-transform duration-200" />
              <span className="hidden sm:inline">Search</span>
            </motion.div>
          )}
        </AnimatePresence>
      </Button>
    </motion.div>
  );
} 